import { SmgElement } from '../core/smg-element';
import { each, html, when, type SafeHtml } from '../core/safe-html';
import { deviceDetailPath, navigate } from '../core/router';
import {
  diskLevel,
  emmcLevel,
  hex2,
  levelColor,
  nfmt,
  shortDuration,
  tempLevel,
} from '../core/format';
import type {
  Device,
  DeviceSortKey,
  DeviceStatus,
  SortDir,
} from '../models/device';
import type { BrandProfile } from '../models/brand';
import { devices, type DeviceListResponse } from '../services/device-service';
import { brands as brandService } from '../services/brand-service';
import { settings, thresholdNumbers } from '../services/settings-service';
import { session } from '../services/auth-service';
import { showToast } from '../services/toast-service';
import { confirmDialog } from '../services/confirm-service';
import { ROLE_ICONS, ROLE_LABELS } from '../models/user';
import { pendingBadge, rescueBadge, wgModeBadge } from '../components/rescue-badge';

/** Liste otomatik yenileme aralığı (kurtarma ilerlemesi canlı aksın) */
const AUTO_REFRESH_MS = 15_000;

type StatusFilter = DeviceStatus | 'all';

interface StatusChipDef {
  key: StatusFilter;
  label: string;
  dot?: string;
  icon?: string;
}

const STATUS_CHIPS: StatusChipDef[] = [
  { key: 'all', label: 'Tümü' },
  { key: 'online', label: 'Çevrimiçi', dot: 'var(--vx-success)' },
  { key: 'offline', label: 'Çevrimdışı', dot: 'var(--vx-text-3)' },
  { key: 'rescue', label: 'Kurtarma', icon: 'ti ti-lifebuoy' },
];

const GRID_COLS =
  '18px minmax(96px,1.4fr) minmax(98px,1fr) minmax(110px,1fr) minmax(84px,.9fr) minmax(110px,1.1fr) 48px 50px 56px 60px 16px';

/** Cihaz listesi — filtre, arama, sayfalama, toplu seçim + toplu işlemler. */
export class DevicesView extends SmgElement {
  private q = '';
  private statusFilter: StatusFilter = 'all';
  private brandFilter: string | 'all' = 'all';
  private page = 1;
  private pageSize = 25;
  /** Kolon sıralaması (sunucu tarafı); null = varsayılan sıra */
  private sortKey: DeviceSortKey | null = null;
  private sortDir: SortDir = 'asc';

  private selectMode = false;
  private selected = new Set<string>();

  private brandMenuOpen = false;
  private selAllOpen = false;
  private assignOpen = false;
  private assignProfile = '';
  private forceStage: 'confirm' | 'sent' | null = null;
  private forceText = '';

  private loading = true;
  private error = '';
  private resp: DeviceListResponse | null = null;
  private brandList: BrandProfile[] = [];

  private searchTimer: ReturnType<typeof setTimeout> | undefined;
  private refreshTimer: ReturnType<typeof setInterval> | undefined;

  protected override onConnected(): void {
    this.wireActs();
    void this.loadAll();
    // Sessiz yenileme: açık diyalog/menü yokken listeyi tazele (progress canlı)
    this.refreshTimer = setInterval(() => {
      if (this.brandMenuOpen || this.selAllOpen || this.assignOpen || this.forceStage) return;
      void this.loadAll(true);
    }, AUTO_REFRESH_MS);
    document.addEventListener('visibilitychange', this.onVisible);
  }

  private onVisible = () => {
    // Sekme arka plandayken tarayıcı zamanlayıcıları kısar; geri dönünce hemen tazele
    if (document.visibilityState === 'visible') void this.loadAll(true);
  };

  protected override onDisconnected(): void {
    clearTimeout(this.searchTimer);
    clearInterval(this.refreshTimer);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  private async loadAll(silent = false): Promise<void> {
    if (!silent) {
      this.loading = true;
      this.error = '';
      this.render();
    }
    try {
      const [resp, brandList] = await Promise.all([
        devices.list(this.query()),
        this.brandList.length ? Promise.resolve(this.brandList) : brandService.list(),
        settings.ensureLoaded(),
      ]);
      this.resp = resp;
      this.brandList = brandList;
      this.error = '';
    } catch {
      if (!silent) this.error = 'Cihaz listesi alınamadı — sunucu bağlantısını kontrol et.';
    } finally {
      this.loading = false;
      // Sessiz yenilemede arama kutusundaki imleci bozmamak için odak korunur
      const active = document.activeElement as HTMLInputElement | null;
      const keepSearch = silent && active?.dataset['act'] === 'search';
      const pos = keepSearch ? active!.selectionStart : null;
      this.render();
      if (keepSearch) {
        const inp = this.qs<HTMLInputElement>('[data-act="search"]');
        inp?.focus();
        if (inp && pos != null) inp.setSelectionRange(pos, pos);
      }
    }
  }

  private reload(): void {
    void this.loadAll();
  }

  private claims() {
    return session.get().claims;
  }

  private totalPages(): number {
    const total = this.resp?.total ?? 0;
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  /** Gridin o anki hali — liste isteği ve CSV dışa aktarma aynı sorguyu kullanır. */
  private query() {
    return {
      q: this.q,
      status: this.statusFilter,
      brand: this.brandFilter,
      page: this.page,
      pageSize: this.pageSize,
      sort: this.sortKey,
      dir: this.sortDir,
    };
  }

  /** Aynı kolona ikinci tık yönü çevirir, üçüncü tık sıralamayı kaldırır. */
  private sortBy(key: DeviceSortKey): void {
    if (this.sortKey !== key) {
      this.sortKey = key;
      // sayısal kolonlarda ilk tık "en yüksek önce" daha kullanışlı (sıcak/dolu cihazlar)
      this.sortDir = key === 'temp' || key === 'disk' || key === 'emmc' ? 'desc' : 'asc';
    } else if (this.sortDir === 'asc') {
      this.sortDir = 'desc';
    } else {
      this.sortKey = null;
      this.sortDir = 'asc';
    }
    this.page = 1;
    this.reload();
  }

  private exportCsv(): void {
    const n = this.resp?.total ?? 0;
    devices.downloadCsv(this.query());
    showToast(`${nfmt(n)} cihaz CSV olarak indiriliyor…`);
  }

  /* ---------------- events ---------------- */

  private wireActs(): void {
    this.onAct('click', {
      chipStatus: (el) => {
        this.statusFilter = (el.dataset['key'] ?? 'all') as StatusFilter;
        this.page = 1;
        this.reload();
      },
      brandMenu: () => {
        this.brandMenuOpen = !this.brandMenuOpen;
        this.render();
      },
      chooseBrand: (el) => {
        this.brandFilter = el.dataset['brand'] ?? 'all';
        this.brandMenuOpen = false;
        this.page = 1;
        this.reload();
      },
      brandSelectAll: (el) => void this.selectWholeBrand(el.dataset['brand'] ?? ''),
      toggleSelect: () => {
        this.selectMode = !this.selectMode;
        this.selected.clear();
        this.render();
      },
      rowTap: (el) => {
        const serial = el.dataset['serial'] ?? '';
        if (this.selectMode) {
          if (this.selected.has(serial)) this.selected.delete(serial);
          else this.selected.add(serial);
          this.render();
        } else {
          navigate(deviceDetailPath(serial));
        }
      },
      openSelAll: () => {
        this.selAllOpen = true;
        this.render();
      },
      closeSelAll: () => {
        this.selAllOpen = false;
        this.render();
      },
      selectPage: () => {
        for (const d of this.resp?.items ?? []) this.selected.add(d.serial);
        this.selAllOpen = false;
        this.render();
      },
      selectFilter: () => void this.selectWholeFilter(),
      clearSel: () => {
        this.selected.clear();
        this.selAllOpen = false;
        this.render();
      },
      bulkRestart: () => void this.bulkRestart(),
      bulkAssign: () => {
        this.assignOpen = true;
        this.assignProfile = this.brandList[0]?.name ?? '';
        this.render();
      },
      bulkForce: () => {
        this.forceStage = 'confirm';
        this.forceText = '';
        this.render();
      },
      closeAssign: () => {
        this.assignOpen = false;
        this.render();
      },
      pickAssign: (el) => {
        this.assignProfile = el.dataset['brand'] ?? '';
        this.render();
      },
      confirmAssign: () => void this.confirmAssign(),
      closeForce: () => {
        this.forceStage = null;
        this.forceText = '';
        this.render();
      },
      confirmForce: () => void this.confirmForce(),
      sortBy: (el) => this.sortBy((el.dataset['key'] ?? 'name') as DeviceSortKey),
      exportCsv: () => this.exportCsv(),
      pageSize: (el) => {
        this.pageSize = Number(el.dataset['n'] ?? 25);
        this.page = 1;
        this.reload();
      },
      goPage: (el) => {
        this.page = Number(el.dataset['n'] ?? 1);
        this.reload();
      },
      prevPage: () => {
        if (this.page > 1) {
          this.page--;
          this.reload();
        }
      },
      nextPage: () => {
        if (this.page < this.totalPages()) {
          this.page++;
          this.reload();
        }
      },
      noop: (_el, ev) => ev.stopPropagation(),
    });

    this.onAct('change', {
      sortSelect: (el) => {
        const v = (el as HTMLSelectElement).value; // "" | key:dir
        if (!v) {
          this.sortKey = null;
          this.sortDir = 'asc';
        } else {
          const [k, d] = v.split(':');
          this.sortKey = k as DeviceSortKey;
          this.sortDir = (d as SortDir) ?? 'asc';
        }
        this.page = 1;
        this.reload();
      },
    });

    this.onAct('input', {
      search: (el) => {
        this.q = (el as HTMLInputElement).value;
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          this.page = 1;
          this.reload();
        }, 300);
      },
      forceText: (el) => {
        this.forceText = (el as HTMLInputElement).value;
        this.qs<HTMLButtonElement>('[data-act="confirmForce"]')?.toggleAttribute(
          'disabled',
          this.forceText.trim() !== 'FORCE INSTALL',
        );
      },
    });
  }

  private async selectWholeFilter(): Promise<void> {
    try {
      const serials = await devices.listSerials({
        q: this.q,
        status: this.statusFilter,
        brand: this.brandFilter,
      });
      this.selected = new Set(serials);
      this.selAllOpen = false;
      showToast(`${nfmt(serials.length)} cihaz seçildi`);
      this.render();
    } catch {
      showToast('Seçim alınamadı', 'warning');
    }
  }

  private async selectWholeBrand(brand: string): Promise<void> {
    if (!brand) return;
    try {
      const serials = await devices.listSerials({ brand });
      this.brandFilter = brand;
      this.brandMenuOpen = false;
      this.selectMode = true;
      this.selected = new Set(serials);
      this.page = 1;
      showToast(`${brand} — ${nfmt(serials.length)} cihaz seçildi`);
      this.reload();
    } catch {
      showToast('Seçim alınamadı', 'warning');
    }
  }

  private async bulkRestart(): Promise<void> {
    const n = this.selected.size;
    if (!n) return;
    const ok = await confirmDialog({
      title: 'Toplu yeniden başlatma',
      message: `${nfmt(n)} seçili cihaza yeniden başlatma komutu gönderilecek. Oynatma kısa süre kesilir.`,
      tone: 'warning',
      icon: 'ti ti-refresh',
      okLabel: `${nfmt(n)} cihazı başlat`,
    });
    if (!ok) return;
    try {
      await devices.bulkCommand([...this.selected], 'restart');
      this.selected.clear();
      this.selectMode = false;
      showToast(`${nfmt(n)} cihaza yeniden başlatma komutu gönderildi`, 'info');
      this.reload();
    } catch {
      showToast('Komut gönderilemedi', 'warning');
    }
  }

  private async confirmAssign(): Promise<void> {
    const n = this.selected.size;
    if (!n || !this.assignProfile) return;
    try {
      await devices.assign([...this.selected], this.assignProfile);
      this.assignOpen = false;
      this.selected.clear();
      this.selectMode = false;
      showToast(`${nfmt(n)} cihaza "${this.assignProfile}" profili atandı`);
      this.reload();
    } catch {
      showToast('Atama başarısız', 'warning');
    }
  }

  private async confirmForce(): Promise<void> {
    if (this.forceText.trim() !== 'FORCE INSTALL') return;
    const n = this.selected.size;
    try {
      await devices.bulkCommand([...this.selected], 'force_install');
      this.forceStage = 'sent';
      this.render();
      this.reload();
    } catch {
      showToast('Komut gönderilemedi', 'warning');
    }
    void n;
  }

  /* ---------------- template ---------------- */

  protected override template(): SafeHtml {
    const s = session.get();
    const c = this.claims();
    const resp = this.resp;
    const total = resp?.total ?? 0;
    const fleetAll = resp?.fleetTotal ?? 0;
    const from = total === 0 ? 0 : (this.page - 1) * this.pageSize + 1;
    const to = Math.min(total, this.page * this.pageSize);

    return html`
      <div class="scr">
        <div style="flex:none;background:var(--vx-surface);border-bottom:1px solid var(--vx-border);padding:16px 16px 12px">
          <div style="display:flex;align-items:center;gap:9px;margin-bottom:12px">
            <span style="font:600 21px var(--vx-font-sans)">Cihazlar</span>
            <span class="chip mono" style="background:var(--vx-chip-bg);color:var(--vx-chip-fg);font-weight:600">
              ${total === fleetAll ? nfmt(total) : `${nfmt(total)} / ${nfmt(fleetAll)}`}
            </span>
            <div style="margin-left:auto;display:flex;align-items:center;gap:8px">
              ${when(s.user, () => html`
                <span class="fchip m-only" style="cursor:default">
                  <i class="${ROLE_ICONS[s.user!.role]}" style="font-size:13px;color:var(--vx-primary)"></i>${ROLE_LABELS[s.user!.role]}
                </span>`)}
              <button class="icobtn" data-act="exportCsv" title="Dışa aktar (CSV) — filtreye uyan ${nfmt(total)} cihaz, sayfadan bağımsız">
                <i class="ti ti-download" style="font-size:18px"></i>
              </button>
              <button class="icobtn ${this.selectMode ? 'on' : ''}" data-act="toggleSelect" title="Toplu seçim">
                <i class="ti ti-checkbox" style="font-size:18px"></i>
              </button>
            </div>
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:9px;align-items:center">
            <div style="position:relative;flex:1 1 230px;min-width:200px">
              <i class="ti ti-search" style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--vx-text-3);font-size:16px"></i>
              <input class="inp" style="padding-left:36px;height:40px" data-act="search"
                     placeholder="Seri no, donanım kimliği, cihaz adı, deviceId veya marka ara" value="${this.q}">
            </div>
            <div class="noscb" style="display:flex;gap:7px;overflow:auto;flex:0 1 auto">
              ${each(STATUS_CHIPS, (chip) => this.statusChip(chip))}
              <button class="fchip ${this.brandFilter !== 'all' ? 'on' : ''}" data-act="brandMenu">
                <i class="ti ti-tag" style="font-size:13px"></i>${this.brandFilter === 'all' ? 'Marka' : this.brandFilter}
                <i class="ti ti-chevron-down" style="font-size:13px"></i>
              </button>
              ${this.mobileSortSelect()}
            </div>
          </div>
        </div>

        ${when(this.selectMode, () => this.bulkBar(c))}
        ${this.listBody()}
        ${this.pagination(from, to, total)}

        ${when(this.brandMenuOpen, () => this.brandMenu())}
        ${when(this.selAllOpen, () => this.selAllMenu())}
        ${when(this.assignOpen, () => this.assignDialog())}
        ${when(this.forceStage, () => this.forceDialog())}
      </div>`;
  }

  private statusChip(chip: StatusChipDef): SafeHtml {
    const counts = this.resp?.counts;
    const count = counts ? counts[chip.key === 'all' ? 'all' : chip.key] : 0;
    const on = this.statusFilter === chip.key;
    return html`
      <button class="fchip ${on ? 'on' : ''}" data-act="chipStatus" data-key="${chip.key}">
        ${when(chip.dot, () => html`<span style="width:7px;height:7px;border-radius:50%;background:${chip.dot!}"></span>`)}
        ${when(chip.icon, () => html`<i class="${chip.icon!}" style="font-size:13px;color:${on ? '#fff' : 'var(--vx-danger)'}"></i>`)}
        ${chip.label}<span class="mono" style="opacity:.7;font-size:11px">${nfmt(count)}</span>
      </button>`;
  }

  private bulkBar(c: ReturnType<DevicesView['claims']>): SafeHtml {
    const filterParts: string[] = [];
    if (this.brandFilter !== 'all') filterParts.push(this.brandFilter);
    if (this.statusFilter !== 'all') {
      filterParts.push(
        { online: 'çevrimiçi', offline: 'çevrimdışı', rescue: 'kurtarma' }[this.statusFilter],
      );
    }
    if (this.q.trim()) filterParts.push(`"${this.q.trim()}"`);
    const bulkBtn =
      'display:flex;align-items:center;gap:5px;font:500 12px var(--vx-font-sans);padding:7px 11px;border-radius:var(--vx-radius-md);background:rgba(255,255,255,.13);color:#fff;border:none;cursor:pointer';

    return html`
      <div style="flex:none;background:var(--vx-ink);color:var(--vx-on-ink);padding:10px 14px;display:flex;flex-wrap:wrap;align-items:center;gap:9px">
        <span style="font:600 13.5px var(--vx-font-sans)">${nfmt(this.selected.size)} cihaz seçili</span>
        ${when(filterParts.length, () => html`
          <span class="chip" style="background:rgba(255,255,255,.14);color:#fff">
            <i class="ti ti-filter" style="font-size:12px"></i>filtre: ${filterParts.join(' · ')}
          </span>`)}
        <button data-act="openSelAll" style="${bulkBtn}"><i class="ti ti-checks" style="font-size:14px"></i>Tümünü seç</button>
        <div style="margin-left:auto;display:flex;align-items:center;gap:8px">
          ${when(c?.ops, () => html`
            <button data-act="bulkRestart" style="${bulkBtn}"><i class="ti ti-refresh" style="font-size:14px"></i>Başlat</button>`)}
          ${when(c?.assign, () => html`
            <button data-act="bulkAssign" style="${bulkBtn}"><i class="ti ti-photo-cog" style="font-size:14px"></i>Profil ata</button>`)}
          ${when(c?.force, () => html`
            <button data-act="bulkForce" style="display:flex;align-items:center;gap:5px;font:500 12px var(--vx-font-sans);padding:7px 11px;border-radius:var(--vx-radius-md);background:var(--vx-danger);color:#fff;border:none;cursor:pointer">
              <i class="ti ti-alert-triangle" style="font-size:14px"></i>Force install
            </button>`)}
          <button data-act="toggleSelect" style="background:none;border:none;color:#fff;opacity:.7;cursor:pointer;display:flex">
            <i class="ti ti-x" style="font-size:18px"></i>
          </button>
        </div>
      </div>`;
  }

  private listBody(): SafeHtml {
    if (this.loading) {
      return html`
        <div class="body" style="display:flex;align-items:center;justify-content:center">
          <i class="ti ti-loader-2 spin" style="font-size:26px;color:var(--vx-primary)"></i>
        </div>`;
    }
    if (this.error) {
      return html`
        <div class="body" style="display:flex;align-items:center;justify-content:center;padding:24px">
          <div style="text-align:center;color:var(--vx-text-2)">
            <i class="ti ti-plug-connected-x" style="font-size:30px;color:var(--vx-text-3)"></i>
            <div style="font:500 13.5px var(--vx-font-sans);margin-top:8px">${this.error}</div>
          </div>
        </div>`;
    }
    const items = this.resp?.items ?? [];
    if (!items.length) {
      return html`
        <div class="body" style="display:flex;align-items:center;justify-content:center;padding:24px">
          <div style="text-align:center;color:var(--vx-text-3)">
            <i class="ti ti-database-search" style="font-size:30px"></i>
            <div style="font:500 13.5px var(--vx-font-sans);margin-top:8px">Filtreye uyan cihaz yok.</div>
          </div>
        </div>`;
    }
    return html`
      <div class="d-only" style="flex:none;display:grid;grid-template-columns:${GRID_COLS};gap:12px;align-items:center;padding:6px 20px;background:var(--vx-surface-2);border-bottom:1px solid var(--vx-border);font:600 10.5px var(--vx-font-sans);letter-spacing:.03em;text-transform:uppercase;color:var(--vx-text-3)">
        <span></span>
        ${this.sortHead('name', 'Cihaz adı')}
        ${this.sortHead('serial', 'Seri no')}
        ${this.sortHead('ip', 'İç IP · dış IP')}
        ${this.sortHead('brand', 'Marka / profil')}
        ${this.sortHead('status', 'Durum')}
        ${this.sortHead('temp', 'Isı', true)}
        ${this.sortHead('disk', 'Disk', true)}
        ${this.sortHead('emmc', 'eMMC', true)}
        ${this.sortHead('version', 'Sürüm')}
        <span></span>
      </div>
      <div class="body">${each(this.resp?.items ?? [], (d) => this.rowTemplate(d))}</div>`;
  }

  /** Tıklanabilir kolon başlığı: aktif kolonda yön oku, ikinci tık ters çevirir, üçüncü kaldırır. */
  private sortHead(key: DeviceSortKey, label: string, right = false): SafeHtml {
    const on = this.sortKey === key;
    const icon = !on ? 'ti ti-arrows-sort' : this.sortDir === 'asc' ? 'ti ti-arrow-up' : 'ti ti-arrow-down';
    return html`
      <button data-act="sortBy" data-key="${key}" title="Sırala: ${label}"
              style="display:flex;align-items:center;gap:3px;min-width:0;background:none;border:none;padding:3px 0;cursor:pointer;font:inherit;letter-spacing:inherit;text-transform:inherit;color:${on ? 'var(--vx-primary)' : 'inherit'};justify-content:${right ? 'flex-end' : 'flex-start'};text-align:${right ? 'right' : 'left'}">
        <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${label}</span>
        <i class="${icon}" style="font-size:12px;flex:none;opacity:${on ? 1 : 0.45}"></i>
      </button>`;
  }

  /** Mobilde kolon başlığı yok — sıralama için küçük seçici. */
  private mobileSortSelect(): SafeHtml {
    const cur = this.sortKey ? `${this.sortKey}:${this.sortDir}` : '';
    const opts: Array<[string, string]> = [
      ['', 'Sıralama: varsayılan'],
      ['name:asc', 'Cihaz adı A→Z'],
      ['name:desc', 'Cihaz adı Z→A'],
      ['serial:asc', 'Seri no ↑'],
      ['status:asc', 'Durum (kurtarma → çevrimiçi → çevrimdışı)'],
      ['temp:desc', 'En sıcak önce'],
      ['disk:desc', 'En dolu disk önce'],
      ['emmc:desc', 'eMMC ömrü en yüksek önce'],
      ['lastSeen:desc', 'En uzun süredir görülmeyen önce'],
      ['brand:asc', 'Marka A→Z'],
      ['version:desc', 'Sürüm (yeni → eski)'],
    ];
    return html`
      <select class="fchip m-only" data-act="sortSelect" style="height:32px;padding:0 8px;max-width:190px">
        ${each(opts, ([v, l]) => html`<option value="${v}" ${cur === v ? 'selected' : ''}>${l}</option>`)}
      </select>`;
  }

  private rowTemplate(d: Device): SafeHtml {
    const th = thresholdNumbers();
    const sel = this.selected.has(d.serial);
    const isRescue = d.status === 'rescue';
    const isOffline = d.status === 'offline';
    const online = d.status === 'online';
    const tl = tempLevel(d.health.tempC, th.temp.warn, th.temp.crit);
    const dl = diskLevel(d.health.diskPct, th.disk.warn, th.disk.crit);
    const el = emmcLevel(d.health.emmcLife, th.emmc.warn, th.emmc.crit);
    const worstCrit = [tl, dl, el].includes('crit');
    const worstWarn = [tl, dl, el].includes('warn');
    const dot = isRescue
      ? 'var(--vx-danger)'
      : isOffline
        ? 'var(--vx-text-3)'
        : worstCrit
          ? 'var(--vx-danger)'
          : worstWarn
            ? 'var(--vx-warning)'
            : 'var(--vx-success)';
    const rowBg = isRescue
      ? 'var(--vx-danger-bg)'
      : isOffline
        ? 'var(--vx-surface-2)'
        : sel
          ? 'var(--vx-brand-50)'
          : 'var(--vx-surface)';
    const badge = this.statusBadge(d.status);
    const tempText = online && d.health.tempC != null ? `${d.health.tempC}°` : '—';
    const diskText = d.health.diskPct != null ? `${d.health.diskPct}%` : '—';
    const emmcText = d.health.emmcLife != null ? hex2(d.health.emmcLife) : '—';

    const checkbox = when(this.selectMode, () => html`
      <span class="checkbox ${sel ? 'on' : ''}"><i class="ti ti-check"></i></span>`);
    const dotSpan = when(!this.selectMode, () => html`
      <span style="width:9px;height:9px;border-radius:50%;flex:none;background:${dot};${isRescue ? 'animation:rescuePulse 2s infinite' : ''}"></span>`);
    const localIp = d.net.ethIp || d.net.wifiIp;
    const localIcon = d.net.ethIp ? 'ti ti-plug' : d.net.wifiIp ? 'ti ti-wifi' : 'ti ti-network-off';
    const ipCell = html`
      <span class="mono" style="font-size:11.5px;color:var(--vx-text-2);display:flex;flex-direction:column;gap:1px;min-width:0">
        <span style="display:flex;align-items:center;gap:4px;white-space:nowrap"><i class="${localIcon}" style="font-size:12px;color:var(--vx-text-3)"></i>${localIp ?? '—'}</span>
        <span style="display:flex;align-items:center;gap:4px;white-space:nowrap;color:var(--vx-text-3)"><i class="ti ti-world" style="font-size:12px"></i>${d.net.publicIp ?? '—'}</span>
      </span>`;

    return html`
      <div class="row" style="background:${rowBg}" data-act="rowTap" data-serial="${d.serial}">
        <!-- mobil satır -->
        <div class="m-only" style="padding:11px 15px">
          <div style="display:flex;align-items:center;gap:11px">
            ${checkbox}${dotSpan}
            <div style="flex:1;min-width:0">
              <div style="font:600 14px var(--vx-font-sans);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:${isRescue ? 'var(--vx-danger-text)' : 'var(--vx-text)'}">${d.name}</div>
              <div class="mono" style="font-size:11px;color:${isRescue ? 'var(--vx-danger-text)' : 'var(--vx-text-3)'};margin-top:1px">${d.businessSerial ? `${d.businessSerial} · ` : ''}${d.serial}</div>
            </div>
            <i class="ti ti-chevron-right" style="color:var(--vx-text-3);font-size:18px"></i>
          </div>
          <div style="display:flex;align-items:center;gap:11px;margin-top:8px;padding-left:20px;flex-wrap:wrap">
            <span class="chip" style="background:var(--vx-chip-bg);color:var(--vx-chip-fg)">${d.brand ?? 'profil yok'}</span>
            ${when(isRescue, () => rescueBadge(d, true))}
            ${pendingBadge(d)}
            ${when(isOffline, () => html`
              <span class="chip" style="background:var(--vx-chip-bg);color:var(--vx-chip-fg)"><i class="ti ti-wifi-off" style="font-size:12px"></i>Çevrimdışı · ${shortDuration(d.lastSeenSec)}</span>`)}
            ${when(online, () => html`
              <span class="hs" style="color:${levelColor(tl)}"><i class="ti ti-temperature"></i>${tempText}</span>
              <span class="hs" style="color:${levelColor(dl)}"><i class="ti ti-database"></i>${diskText}</span>
              <span class="hs" style="color:${levelColor(el)}"><i class="ti ti-device-sd-card"></i>${emmcText}</span>`)}
          </div>
          <div style="display:flex;align-items:center;gap:12px;margin-top:6px;padding-left:20px;flex-wrap:wrap">
            <span class="hs mono" style="color:var(--vx-text-3)"><i class="${localIcon}"></i>${localIp ?? '—'}</span>
            <span class="hs mono" style="color:var(--vx-text-3)"><i class="ti ti-world"></i>${d.net.publicIp ?? '—'}</span>
            ${wgModeBadge(d)}
          </div>
        </div>
        <!-- masaüstü satırı -->
        <div class="d-only" style="padding:10px 20px">
          <div style="display:grid;grid-template-columns:${GRID_COLS};gap:12px;align-items:center">
            ${this.selectMode ? checkbox : dotSpan}
            <span style="font:600 13.5px var(--vx-font-sans);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:${isRescue ? 'var(--vx-danger-text)' : 'var(--vx-text)'}">${d.name}</span>
            <span style="min-width:0;display:flex;flex-direction:column;gap:1px" title="Donanım kimliği (eMMC CID): ${d.serial}">
              ${d.businessSerial
                ? html`<span class="mono" style="font-size:12.5px;font-weight:600;color:var(--vx-text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${d.businessSerial}</span>
                       <span class="mono" style="font-size:10.5px;color:var(--vx-text-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${d.serial}</span>`
                : html`<span class="mono" style="font-size:12px;color:var(--vx-text-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${d.serial}</span>
                       <span style="font-size:10.5px;color:var(--vx-text-3)">seri no atanmadı</span>`}
            </span>
            ${ipCell}
            <span class="chip" style="background:var(--vx-chip-bg);color:var(--vx-chip-fg);justify-self:start">${d.brand ?? '—'}</span>
            <span style="justify-self:start;min-width:0">${isRescue ? rescueBadge(d, true) : d.pendingAction ? pendingBadge(d) : html`<span class="chip" style="background:${badge.bg};color:${badge.fg};font-weight:600"><i class="${badge.icon}" style="font-size:12px"></i>${badge.label}</span>`}</span>
            <span class="mono" style="font-size:12.5px;text-align:right;font-weight:500;color:${levelColor(tl)}">${tempText}</span>
            <span class="mono" style="font-size:12.5px;text-align:right;font-weight:500;color:${levelColor(dl)}">${diskText}</span>
            <span class="mono" style="font-size:12.5px;text-align:right;font-weight:500;color:${levelColor(el)}">${emmcText}</span>
            <span class="mono" style="font-size:11.5px;color:var(--vx-text-3)">${d.version ?? '—'}</span>
            <i class="ti ti-chevron-right" style="color:var(--vx-text-3);font-size:17px"></i>
          </div>
        </div>
      </div>`;
  }

  private statusBadge(status: DeviceStatus): { bg: string; fg: string; icon: string; label: string } {
    if (status === 'rescue') {
      return { bg: 'var(--vx-danger)', fg: '#fff', icon: 'ti ti-lifebuoy', label: 'Kurtarma' };
    }
    if (status === 'offline') {
      return { bg: 'var(--vx-chip-bg)', fg: 'var(--vx-chip-fg)', icon: 'ti ti-wifi-off', label: 'Çevrimdışı' };
    }
    return { bg: 'var(--vx-success-bg)', fg: 'var(--vx-success-text)', icon: 'ti ti-point-filled', label: 'Çevrimiçi' };
  }

  private pagination(from: number, to: number, total: number): SafeHtml {
    const totalPages = this.totalPages();
    const nums: Array<{ label: string; n?: number }> = [];
    const push = (n: number) => nums.push({ label: String(n), n });
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) push(i);
    } else {
      push(1);
      if (this.page > 4) nums.push({ label: '…' });
      for (let i = Math.max(2, this.page - 1); i <= Math.min(totalPages - 1, this.page + 1); i++) push(i);
      if (this.page < totalPages - 3) nums.push({ label: '…' });
      push(totalPages);
    }
    const navBtn =
      'width:32px;height:32px;border-radius:var(--vx-radius-md);border:1px solid var(--vx-border);background:var(--vx-surface);display:flex;align-items:center;justify-content:center;cursor:pointer';

    return html`
      <div style="flex:none;background:var(--vx-surface);border-top:1px solid var(--vx-border);padding:11px 16px;display:flex;flex-wrap:wrap;align-items:center;gap:10px">
        <span class="mono" style="font-size:12px;color:var(--vx-text-3)">${nfmt(from)}–${nfmt(to)} / ${nfmt(total)} cihaz</span>
        <div style="display:flex;gap:5px;align-items:center">
          <span style="font:500 11px var(--vx-font-sans);color:var(--vx-text-3)">sayfa</span>
          ${each([25, 50, 100], (n) => html`
            <button class="fchip ${this.pageSize === n ? 'on' : ''}" style="padding:5px 9px;font-size:11.5px" data-act="pageSize" data-n="${n}">${n}</button>`)}
        </div>
        <div style="margin-left:auto;display:flex;gap:5px;align-items:center">
          <button data-act="prevPage" style="${navBtn};color:${this.page > 1 ? 'var(--vx-text-2)' : 'var(--vx-text-3)'}"><i class="ti ti-chevron-left"></i></button>
          ${each(nums, (p) => html`
            <button data-act="${p.n ? 'goPage' : 'noop'}" data-n="${p.n ?? ''}"
                    style="min-width:32px;height:32px;padding:0 7px;border-radius:var(--vx-radius-md);border:1px solid ${p.n === this.page ? 'var(--vx-primary)' : 'var(--vx-border)'};background:${p.n === this.page ? 'var(--vx-primary)' : 'var(--vx-surface)'};color:${!p.n ? 'var(--vx-text-3)' : p.n === this.page ? '#fff' : 'var(--vx-text-2)'};font:600 12px var(--vx-font-sans);cursor:${p.n ? 'pointer' : 'default'};font-variant-numeric:tabular-nums">${p.label}</button>`)}
          <button data-act="nextPage" style="${navBtn};color:${this.page < totalPages ? 'var(--vx-text-2)' : 'var(--vx-text-3)'}"><i class="ti ti-chevron-right"></i></button>
        </div>
      </div>`;
  }

  /* ---------------- overlays ---------------- */

  private brandMenu(): SafeHtml {
    const options: Array<{ key: string; label: string; count: number; canSelect: boolean }> = [
      { key: 'all', label: 'Tümü', count: this.resp?.fleetTotal ?? 0, canSelect: false },
      ...this.brandList.map((b) => ({
        key: b.name,
        label: b.name,
        count: b.deviceCount,
        canSelect: true,
      })),
    ];
    return html`
      <div class="ovl menu" data-act="brandMenu">
        <div class="sheet" data-act="noop" style="padding:8px 8px 14px;max-height:520px;overflow:auto">
          <div style="padding:10px 10px 8px;font:600 11px var(--vx-font-sans);color:var(--vx-text-3);text-transform:uppercase;letter-spacing:.04em">Marka / profil filtresi</div>
          ${each(options, (o) => {
            const active = this.brandFilter === o.key;
            return html`
              <div style="display:flex;align-items:center;gap:4px">
                <button class="mrow" style="background:${active ? 'var(--vx-brand-50)' : 'transparent'}" data-act="chooseBrand" data-brand="${o.key}">
                  <i class="ti ti-tag" style="font-size:17px;color:${active ? 'var(--vx-primary)' : 'var(--vx-text-3)'}"></i>
                  <span style="flex:1;font:500 14px var(--vx-font-sans);color:${active ? 'var(--vx-brand-700)' : 'var(--vx-text)'}">${o.label}</span>
                  <span class="mono" style="font-size:12px;color:var(--vx-text-3)">${nfmt(o.count)}</span>
                  ${when(active, () => html`<i class="ti ti-check" style="font-size:17px;color:var(--vx-primary)"></i>`)}
                </button>
                ${when(o.canSelect, () => html`
                  <button class="btn sec sm" style="flex:none;padding:0 10px" data-act="brandSelectAll" data-brand="${o.key}">
                    <i class="ti ti-checks"></i>Seç
                  </button>`)}
              </div>`;
          })}
        </div>
      </div>`;
  }

  private selAllMenu(): SafeHtml {
    const pageCount = this.resp?.items.length ?? 0;
    return html`
      <div class="ovl menu" data-act="closeSelAll" style="animation:fadeIn .15s">
        <div class="sheet" data-act="noop">
          <div style="padding:17px 20px;border-bottom:1px solid var(--vx-border);display:flex;align-items:center;gap:9px">
            <i class="ti ti-checks" style="font-size:20px;color:var(--vx-primary)"></i>
            <span style="font:600 16.5px var(--vx-font-sans)">Tümünü seç</span>
          </div>
          <div style="padding:14px 16px;display:flex;flex-direction:column;gap:9px">
            <button class="mrow" style="border:1px solid var(--vx-border)" data-act="selectPage">
              <i class="ti ti-file-check" style="font-size:19px;color:var(--vx-text-2)"></i>
              <span style="flex:1"><span style="display:block;font:600 13.5px var(--vx-font-sans)">Bu sayfadakiler</span>
                <span style="font:400 11.5px var(--vx-font-sans);color:var(--vx-text-3)">${nfmt(pageCount)} cihaz — sayfa ${this.page}/${this.totalPages()}</span></span>
            </button>
            <button class="mrow" style="border:1px solid var(--vx-primary);background:var(--vx-brand-50)" data-act="selectFilter">
              <i class="ti ti-filter-check" style="font-size:19px;color:var(--vx-primary)"></i>
              <span style="flex:1"><span style="display:block;font:600 13.5px var(--vx-font-sans);color:var(--vx-brand-700)">Filtredeki tüm cihazlar</span>
                <span style="font:400 11.5px var(--vx-font-sans);color:var(--vx-text-2)">${nfmt(this.resp?.total ?? 0)} cihaz — mevcut filtre kapsamı</span></span>
            </button>
            <button class="mrow" style="border:1px solid var(--vx-border)" data-act="clearSel">
              <i class="ti ti-square-off" style="font-size:19px;color:var(--vx-text-2)"></i>
              <span style="flex:1"><span style="display:block;font:600 13.5px var(--vx-font-sans)">Seçimi temizle</span></span>
            </button>
          </div>
        </div>
      </div>`;
  }

  private assignDialog(): SafeHtml {
    const n = this.selected.size;
    return html`
      <div class="ovl menu" data-act="closeAssign" style="animation:fadeIn .15s">
        <div class="sheet wide" data-act="noop" style="max-height:92%;display:flex;flex-direction:column">
          <div style="padding:17px 20px;border-bottom:1px solid var(--vx-border);display:flex;align-items:center;gap:9px;flex:none">
            <i class="ti ti-photo-cog" style="font-size:20px;color:var(--vx-primary)"></i>
            <span style="font:600 17px var(--vx-font-sans)">İmaj profili ata</span>
          </div>
          <div class="body" style="padding:20px">
            <div style="font:400 13.5px/1.5 var(--vx-font-sans);color:var(--vx-text-2);margin-bottom:14px">${nfmt(n)} seçili cihaza yeni imaj profili atanacak.</div>
            <label class="lbl">Profil</label>
            <div style="display:flex;flex-direction:column;gap:8px">
              ${each(this.brandList, (b) => {
                const active = this.assignProfile === b.name;
                return html`
                  <button data-act="pickAssign" data-brand="${b.name}"
                          style="display:flex;align-items:center;gap:11px;padding:12px;border-radius:var(--vx-radius-md);border:1px solid ${active ? 'var(--vx-primary)' : 'var(--vx-border)'};background:${active ? 'var(--vx-brand-50)' : 'var(--vx-surface)'};cursor:pointer;text-align:left">
                    <span style="width:18px;height:18px;border-radius:50%;border:2px solid ${active ? 'var(--vx-primary)' : 'var(--vx-border-strong)'};display:flex;align-items:center;justify-content:center;flex:none">
                      ${when(active, () => html`<span style="width:9px;height:9px;border-radius:50%;background:var(--vx-primary)"></span>`)}
                    </span>
                    <span style="flex:1;min-width:0">
                      <span style="display:block;font:600 13.5px var(--vx-font-sans)">${b.name} — default</span>
                      <span class="mono" style="font-size:11px;color:var(--vx-text-3)">${b.imageFile} · ${b.version}</span>
                    </span>
                  </button>`;
              })}
            </div>
            <div style="display:flex;gap:8px;margin-top:14px;padding:11px 12px;background:var(--vx-info-bg);border-radius:var(--vx-radius-md)">
              <i class="ti ti-info-circle" style="color:var(--vx-info);font-size:16px;flex:none"></i>
              <span style="font:400 11.5px/1.5 var(--vx-font-sans);color:var(--vx-info-text)">Atama sonrası cihazda imaj indirilir ve smg-player kurulum scripti çalışır.</span>
            </div>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px;padding:14px 20px;border-top:1px solid var(--vx-border);flex:none">
            <button class="btn sec" data-act="closeAssign">İptal</button>
            <button class="btn pri" data-act="confirmAssign">${nfmt(n)} cihaza ata</button>
          </div>
        </div>
      </div>`;
  }

  private forceDialog(): SafeHtml {
    const n = this.selected.size;
    if (this.forceStage === 'sent') {
      return html`
        <div class="ovl menu" style="animation:fadeIn .15s">
          <div class="sheet wide" data-act="noop">
            <div style="padding:26px 22px;text-align:center">
              <span style="width:54px;height:54px;border-radius:50%;background:var(--vx-success-bg);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px">
                <i class="ti ti-check" style="font-size:28px;color:var(--vx-success)"></i>
              </span>
              <div style="font:600 17px var(--vx-font-sans);margin-bottom:5px">Komut gönderildi</div>
              <p style="font:400 13px/1.5 var(--vx-font-sans);color:var(--vx-text-2);margin:0 0 18px">
                ${nfmt(n)} cihaz kurtarma moduna alınıp marka default imajıyla sıfırdan kurulacak.
                İlerlemeyi listedeki durum rozetlerinden izleyebilirsin.
              </p>
              <button class="btn pri" style="width:100%" data-act="closeForce">Kapat</button>
            </div>
          </div>
        </div>`;
    }
    return html`
      <div class="ovl menu" data-act="closeForce" style="animation:fadeIn .15s">
        <div class="sheet wide" data-act="noop">
          <div style="padding:20px 20px 0;display:flex;align-items:center;gap:11px">
            <span style="width:40px;height:40px;border-radius:50%;background:var(--vx-danger-bg);display:flex;align-items:center;justify-content:center;flex:none">
              <i class="ti ti-alert-triangle" style="font-size:22px;color:var(--vx-danger)"></i>
            </span>
            <span style="font:600 17px var(--vx-font-sans);color:var(--vx-danger-text)">Yeniden kurulum — force install</span>
          </div>
          <div style="padding:14px 20px 20px">
            <p style="font:400 13.5px/1.6 var(--vx-font-sans);color:var(--vx-text-2);margin:0 0 14px">
              Bu işlem seçili ${nfmt(n)} cihazı kurtarma moduna alıp marka default imajını sıfırdan yazar.
              Cihazdaki tüm yerel durum silinir ve işlem <b style="color:var(--vx-danger-text)">geri alınamaz</b>.
            </p>
            <label class="lbl">Onaylamak için yaz: <span class="mono" style="color:var(--vx-text)">FORCE INSTALL</span></label>
            <input class="inp" data-act="forceText" placeholder="FORCE INSTALL" value="${this.forceText}">
          </div>
          <div style="display:flex;justify-content:flex-end;gap:10px;padding:0 20px 20px">
            <button class="btn sec" data-act="closeForce">Vazgeç</button>
            <button class="btn dng" data-act="confirmForce" disabled><i class="ti ti-reload"></i>Yeniden kur</button>
          </div>
        </div>
      </div>`;
  }
}

customElements.define('smg-devices-view', DevicesView);
